import { useEffect, useState, useContext, useMemo } from 'react';
import styles from './Table.module.scss';
import classNames from 'classnames/bind';
import { Button, Grid, Select, Table as TableTeam, Tooltip } from 'antd';
import DropdownGroup from '~/components/dropdownGroup/dropdownGroup';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { LoadingContext } from '~/contexts/LoadingContext';
import { selectListTable } from '~/thunks/table/tableSlice';
import { selectListTournament } from '~/thunks/tournament/tournamentSlice';
import { convertDataSourceLeague } from '~/utils/helper';
import { IQueryGetTables } from '~/apis/table';
import { getTable } from '~/thunks/table/tableThunck';
import { getTournament } from '~/thunks/tournament/tournamentThunk';
import { Link } from 'react-router-dom';
import { RouterPaths, StorageConstants } from '~/utils/enum';

const cx = classNames.bind(styles);

const styleColumnOrder = { color: 'green', fontWeight: 'bold' };

interface ITournament {
  label: string;
  value: string;
}

interface TournamentResponse {
  id: string;
  name: string;
  year: string;
}

interface IDataType {
  key: string;
  rank: number;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsScored: number;
  goalsConceded: number;
  goalDifference: number;
  point: number;
  team: string;
  fantasyPoint: number;
}

const Table = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loadingContext = useContext(LoadingContext);
  const dataSourceTables = useAppSelector(selectListTable);
  const dataSourceLeagues = useAppSelector(selectListTournament);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [leagueSelected, setLeagueSelected] = useState<string>();
  const [dataLeagues, setDataLeagues] = useState<ITournament[]>([]);
  const [dataTables, setDataTables] = useState<IDataType[]>([]);
  const [groupSelected, setGroupSelected] = useState<string>('');

  const columns: ColumnsType<IDataType> = useMemo(() => {
    const pointColumn: ColumnType<IDataType> = {
      title: (
        <span className={cx('table_content')}>
          {groupSelected !== '' ? (
            <span style={styleColumnOrder}>{t('header_table_point')}</span>
          ) : (
            t('header_table_point')
          )}
        </span>
      ),
      dataIndex: 'point',
      width: isMobile ? '100px' : 'fit-content',
      className: cx('table_align_center'),
      render: (text) => {
        if (groupSelected !== '') {
          return (
            <span className={cx('table_content')} style={styleColumnOrder}>
              {text}
            </span>
          );
        }
        return <span className={cx('table_content')}>{text}</span>;
      },
    };
    const fantasyPointColumn: ColumnType<IDataType> = {
      title: (
        <span className={cx('table_content')}>
          {groupSelected === '' ? (
            <span style={styleColumnOrder}>{t('header_table_fantasy_point')}</span>
          ) : (
            t('header_table_fantasy_point')
          )}
        </span>
      ),
      dataIndex: 'fantasyPoint',
      width: isMobile ? '100px' : '150px',
      className: cx('table_align_center'),
      render: (text) => {
        if (groupSelected === '') {
          return (
            <span className={cx('table_content')} style={styleColumnOrder}>
              {text}
            </span>
          );
        }
        return <span className={cx('table_content')}>{text}</span>;
      },
    };

    return [
      {
        title: <span className={cx('table_content')}>#</span>,
        dataIndex: 'rank',
        className: cx('table_align_center'),
        width: isMobile ? '30px' : '50px',
        fixed: isMobile ? 'left' : undefined,
        render: (text) => <span className={cx('table_content')}>{text}</span>,
      },
      {
        title: <span className={cx('table_content')}>{t('header_table_team')}</span>,
        dataIndex: 'team',
        width: isMobile ? '100px' : '35%',
        fixed: isMobile ? 'left' : undefined,
        render: (text) => <span className={cx('table_content')}>{text}</span>,
      },
      ...(isMobile
        ? groupSelected !== ''
          ? [pointColumn, fantasyPointColumn]
          : [fantasyPointColumn, pointColumn]
        : []),
      {
        title: <span className={cx('table_content')}>{t('header_table_played')}</span>,
        dataIndex: 'played',
        width: isMobile ? '100px' : 'fit-content',
        className: cx('table_align_center'),
        render: (text) => <span className={cx('table_content')}>{text}</span>,
      },
      {
        title: <span className={cx('table_content')}>{t('header_table_won')}</span>,
        dataIndex: 'won',
        width: isMobile ? '100px' : 'fit-content',
        className: cx('table_align_center'),
        render: (text) => <span className={cx('table_content')}>{text}</span>,
      },
      {
        title: <span className={cx('table_content')}>{t('header_table_drawn')}</span>,
        dataIndex: 'drawn',
        width: isMobile ? '100px' : 'fit-content',
        className: cx('table_align_center'),
        render: (text) => <span className={cx('table_content')}>{text}</span>,
      },
      {
        title: <span className={cx('table_content')}>{t('header_table_lost')}</span>,
        dataIndex: 'lost',
        width: isMobile ? '100px' : 'fit-content',
        className: cx('table_align_center'),
        render: (text) => <span className={cx('table_content')}>{text}</span>,
      },
      {
        title: (
          <Tooltip title={t('header_table_gs_tooltip')}>
            <span className={cx('table_content')}>{t('header_table_gs')}</span>
          </Tooltip>
        ),
        dataIndex: 'goalsScored',
        width: isMobile ? '100px' : 'fit-content',
        className: cx('table_align_center'),
        render: (text) => <span className={cx('table_content')}>{text}</span>,
      },
      {
        title: (
          <Tooltip title={t('header_table_gc_tooltip')}>
            <span className={cx('table_content')}>{t('header_table_gc')}</span>
          </Tooltip>
        ),
        dataIndex: 'goalsConceded',
        width: isMobile ? '100px' : 'fit-content',
        className: cx('table_align_center'),
        render: (text) => <span className={cx('table_content')}>{text}</span>,
      },
      {
        title: (
          <Tooltip title={t('header_table_gd_tooltip')}>
            <span className={cx('table_content')}>{t('header_table_gd')}</span>
          </Tooltip>
        ),
        dataIndex: 'goalDifference',
        width: isMobile ? '100px' : 'fit-content',
        className: cx('table_align_center'),
        render: (text) => <span className={cx('table_content')}>{text}</span>,
      },
      ...(!isMobile ? [pointColumn, fantasyPointColumn] : []),
    ];
  }, [groupSelected, isMobile, t]);

  useEffect(() => {
    handleGetTournament();
  }, []);

  useEffect(() => {
    if (!leagueSelected) {
      return;
    }

    localStorage.setItem(StorageConstants.LEAGUE_SELECTED, leagueSelected);
    handleGetTable({ tournamentId: leagueSelected, groupName: groupSelected });
  }, [leagueSelected, groupSelected]);

  useEffect(() => {
    if (!dataSourceLeagues || dataSourceLeagues?.length < 0) {
      return;
    }
    const dataSource = convertDataSourceLeague(dataSourceLeagues);
    setDataLeagues(dataSource);
  }, [dataSourceLeagues]);

  useEffect(() => {
    if (!dataSourceTables || dataSourceTables?.length <= 0) {
      setDataTables([]);
      return;
    }
    const dataSource = dataSourceTables?.map((data: any, index: number) => ({
      key: data?.id,
      rank: index + 1,
      played: Number(data?.win) + Number(data?.lose) + Number(data?.draw),
      won: data?.win,
      drawn: data?.draw,
      lost: data?.lose,
      goalsScored: data?.goalScore,
      goalsConceded: data?.goalConcede,
      goalDifference: data?.goalDifference,
      point: data?.rankingPoint,
      team: data?.name,
      fantasyPoint: Number(data?.totalPoint),
    }));

    setDataTables(dataSource);
  }, [dataSourceTables]);

  const customRow = (record: IDataType) => {
    if (record.rank === 1) {
      return cx('row_one');
    }
    if (record.rank === 2) {
      return cx('row_two');
    }
    if (record.rank === 3) {
      return cx('row_three');
    }
    return '';
  };

  //#region Handle Function
  const handleChangeGroup = (valueChange: string) => {
    loadingContext?.show();
    setGroupSelected(valueChange);
  };

  const handleChangeLeague = (value: string) => {
    loadingContext?.show();
    setLeagueSelected(value);
  };

  const handleGetTable = (payload: IQueryGetTables) => {
    dispatch(getTable(payload))
      .unwrap()
      .then((response) => {
        loadingContext?.hide();
      })
      .catch((error) => {
        loadingContext?.hide();
      });
  };

  const handleGetTournament = () => {
    loadingContext?.show();
    dispatch(getTournament())
      .unwrap()
      .then((res) => {
        const { tournament } = res.data;
        if (tournament && tournament?.length > 0) {
          const storageLeagueSelected = localStorage.getItem(StorageConstants.LEAGUE_SELECTED);
          const existLeague = tournament.some(
            (item: TournamentResponse) => item.id === storageLeagueSelected
          );
          const tournamentIdSelected = existLeague ? storageLeagueSelected : tournament[0]?.id;
          setLeagueSelected(tournamentIdSelected);
          return;
        }
        loadingContext?.hide();
      })
      .catch((err) => {
        loadingContext?.hide();
      });
  };
  //#endregion Handle Function

  return (
    <div className={cx('table_container')}>
      <div className={cx('table_content_top')}>
        <div className={cx('table_select_container')}>
          <div className={cx('table_select_league_container')}>
            <span className={cx('select_label_table')}>{t('select_label_table')}: </span>
            <Select
              onChange={handleChangeLeague}
              className={cx('table_select')}
              placeholder={t('placeholder_select_tournament')}
              value={leagueSelected}
              options={dataLeagues}
            />
          </div>

          <div className={cx('table_select_group')}>
            <DropdownGroup
              defaultValue={groupSelected}
              onChange={handleChangeGroup}
              width={isMobile ? '100%' : undefined}
            />
          </div>
        </div>
        <Link className={cx('table_link_button')} to={'/' + RouterPaths.PUBLIC.GROUP}>
          <Button block>{t('link_to_page_public_group')}</Button>
        </Link>
      </div>
      <div className={cx('table_wrapper')}>
        <TableTeam
          columns={columns}
          dataSource={dataTables}
          className={cx('table_team')}
          pagination={false}
          scroll={{
            y: !isMobile ? 'calc(100vh - 279px)' : undefined,
            x: isMobile ? 'fit-content' : 1500,
          }}
          rowClassName={customRow}
        />
      </div>
    </div>
  );
};

export default Table;
